import { type ReactNode } from 'react';

import { Box, Button, Card, Chip, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { CAM_COMPACT } from '@/cam-admin/responsive';

// '← Actions' style back affordance used by every detail view.
export const BackLink = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <Button
    variant='text'
    color='primary'
    startIcon={<Icon icon='chevron-left' fontSize={14} />}
    onClick={onClick}
    style={{ alignSelf: 'flex-start' }}
  >
    {label}
  </Button>
);

// A page you step into over the whole shell, and come back from: the
// notifications feed, a care provider. Slides up, and its Back sits in a
// full-bleed band so the rule runs the width of the frame, as production has
// it. Absolute against CamShell's root, so the app bar and rail go under it.
export const ShellOverlay = ({
  onBack,
  children,
}: {
  onBack: () => void;
  children: ReactNode;
}) => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      zIndex: 5,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: 'background.default',
      // On mount: it only exists while it is open, so there is nothing to
      // transition from.
      '@keyframes shellOverlayIn': {
        from: { transform: 'translateY(100%)' },
        to: { transform: 'translateY(0)' },
      },
      animation: 'shellOverlayIn 260ms cubic-bezier(0.22, 1, 0.36, 1)',
    }}
  >
    <Box
      sx={{
        px: 3,
        py: 1.5,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'custom.neutral.200',
        [CAM_COMPACT]: { px: 2 },
      }}
    >
      <BackLink label='Back' onClick={onBack} />
    </Box>

    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        p: 3,
        [CAM_COMPACT]: { p: 2 },
      }}
    >
      {children}
    </Box>
  </Box>
);

// The empty slot in a row of chips: a dashed outline, a plus and a word. Used
// where something can be added to a set that is already shown as chips — a
// circle's watchers, its case manager. Dashed because it is a slot to fill
// rather than a thing that is there.
export const AddChip = ({
  label = 'Add',
  disabled,
  onClick,
}: {
  label?: string;
  disabled?: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}) => (
  <Chip
    variant='outlined'
    size='small'
    icon={<Icon icon='plus' fontSize={12} />}
    label={label}
    disabled={disabled}
    onClick={onClick}
    sx={{ borderStyle: 'dashed' }}
  />
);

// Overline group label ('OVERDUE', 'UPCOMING', …). Overdue groups go red.
export const GroupLabel = ({
  children,
  error,
}: {
  children: React.ReactNode;
  error?: boolean;
}) => (
  // In sx, not Typography's color prop: llama bakes a literal colour into every
  // typography variant at import time, so the prop is inert and OVERDUE came
  // out the same grey as UPCOMING.
  <Typography
    sx={{ color: error ? 'error.main' : 'text.secondary' }}
    variant='overline'
  >
    {children}
  </Typography>
);

// Floral White panel with a Lora heading, like the Figma control panel cards
// ('Call summary', 'Linked documents', …).
export const PanelCard = ({
  title,
  action,
  children,
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card sx={{ p: 2 }}>
    <Stack spacing={2}>
      {title && (
        // Same as HeaderCard's band: the action must not set the row's height.
        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            '& .MuiButton-root': { minHeight: 0, py: 0.25 },
          }}
          direction='row'
        >
          <Typography variant='h6' color='primary.main'>
            {title}
          </Typography>
          {action}
        </Stack>
      )}
      {children}
    </Stack>
  </Card>
);

// A card split into a header and a body by a full-width stroke (edge to edge,
// not an inset Divider) — the overview cards. The stroke is the card's own
// border colour, so header + body read as two stacked cards fused into one.
// Pass `title`/`action` for the standard header row, or `header` for custom
// header content (e.g. an avatar + name, or a title with a subtitle).
export const HeaderCard = ({
  title,
  action,
  header,
  children,
}: {
  title?: string;
  action?: React.ReactNode;
  header?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card sx={{ p: 0, overflow: 'hidden' }}>
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'custom.neutral.200' }}>
      {header ?? (
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
        >
          <Typography variant='h6' color='primary.main'>
            {title}
          </Typography>
          {action}
        </Stack>
      )}
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Card>
);

// The detail-page layout: a flexible main column with a fixed side rail,
// matching the create/view screens in Figma.
export const TwoColumn = ({
  main,
  rail,
}: {
  main: React.ReactNode;
  rail: React.ReactNode;
}) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 2 }}>
    <Box sx={{ minWidth: 0 }}>{main}</Box>
    <Stack spacing={2}>{rail}</Stack>
  </Box>
);
